import { localDayStart, type AuditQuery, type Tally } from './api'
import { AUDIT_GROUPS, auditGroup, auditLabel } from './auditLabels'

// What the Audit trail's filter bar holds, and the read it stands for.
//
// # WHY THIS IS NOT IN THE SCREEN
//
// Until 11 October 2026 the filter was a panel of every act the chain holds,
// open above the trail, and the trail's first row began 700 px down the page.
// The acts moved behind one control and the boxes onto one row. What must not
// move with them is the read itself: the same choices send the API the same
// query they always did. That is a claim a test can hold only if the step from
// what the controls hold to what is sent is one function, so it is this one.

// The controls' own values: the acts ticked, in the order they were ticked,
// the three typed boxes as typed, and the two calendar dates as a date control
// gives them (YYYY-MM-DD, or empty).
export type AuditChoices = {
  acts: string[]
  actor: string
  entityType: string
  entityId: string
  since: string
  until: string
}

export const NO_CHOICES: AuditChoices = { acts: [], actor: '', entityType: '', entityId: '', since: '', until: '' }

export function auditQueryFrom(c: AuditChoices): AuditQuery {
  return {
    action: c.acts.length > 0 ? c.acts : undefined,
    actor: c.actor.trim() || undefined,
    entityType: c.entityType.trim() || undefined,
    entityId: c.entityId.trim() || undefined,
    // Sent as instants and not as the calendar dates the controls hold. See
    // localDayStart: a bare date is read at the other end as UTC midnight, so
    // in India the window used to open five and a half hours into the day it
    // named and close five and a half hours into the day it excluded, keeping
    // and dropping rows with nothing on the screen to say it had happened.
    //
    // until is the start of the day after the one named, which is what makes
    // the second box read as On or before rather than as an exclusive edge
    // nobody expects from a date range. The API compares the two and refuses
    // a window that ends before it starts, so under the old reading naming
    // one day in both boxes was refused outright.
    since: localDayStart(c.since),
    until: localDayStart(c.until, 1),
  }
}

export function toggleAct(c: AuditChoices, key: string): AuditChoices {
  return { ...c, acts: c.acts.includes(key) ? c.acts.filter((a) => a !== key) : [...c.acts, key] }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// A calendar date as the trail prints one, read off its own digits. Through a
// Date it would be midnight UTC, which is the evening before for a reader west
// of Greenwich, and the chip would name a day the control does not.
function dayWords(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined
  return m && month ? m[3] + ' ' + month + ' ' + m[1] : date
}

// One thing the trail is narrowed by, as the bar says it, and the choices
// without it: pressing the chip is that removal.
export type AuditChip = { key: string; label: string; title?: string; without: AuditChoices }

export function auditChips(c: AuditChoices): AuditChip[] {
  const out: AuditChip[] = c.acts.map((key) => ({
    key: 'act:' + key,
    label: auditLabel(key),
    title: key,
    without: { ...c, acts: c.acts.filter((a) => a !== key) },
  }))
  if (c.actor.trim()) out.push({ key: 'actor', label: 'Actor: ' + c.actor.trim(), without: { ...c, actor: '' } })
  if (c.entityType.trim()) out.push({ key: 'entityType', label: 'Kind: ' + c.entityType.trim(), without: { ...c, entityType: '' } })
  if (c.entityId.trim()) out.push({ key: 'entityId', label: 'Record: ' + c.entityId.trim(), without: { ...c, entityId: '' } })
  if (localDayStart(c.since)) out.push({ key: 'since', label: 'On or after ' + dayWords(c.since), without: { ...c, since: '' } })
  if (localDayStart(c.until)) out.push({ key: 'until', label: 'On or before ' + dayWords(c.until), without: { ...c, until: '' } })
  return out
}

// What the Acts control says on the bar, closed. The number is the point: an
// act ticked inside a closed list is otherwise a filter nobody can see.
export function actsSummary(chosen: number): string {
  return chosen === 0 ? 'Any act' : chosen + ' chosen'
}

// The chain's acts under the product's own groups, in the order the product
// is arranged, most frequent first inside each. An act the label map does not
// know yet is kept, under Other, rather than hidden. Typing narrows by the
// act's words, its code or its group, so "tier", "vendor.tier" and "exits"
// each find what they name.
export function actGroups(actions: Tally[], typed = ''): [string, Tally[]][] {
  const words = typed.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const by = new Map<string, Tally[]>()
  for (const a of actions) {
    const group = auditGroup(a.key)
    const hay = (auditLabel(a.key) + ' ' + a.key + ' ' + group).toLowerCase()
    if (!words.every((w) => hay.includes(w))) continue
    by.set(group, [...(by.get(group) ?? []), a])
  }
  const order: string[] = [...AUDIT_GROUPS, 'Other']
  return order
    .filter((g) => by.has(g))
    .map((g) => [g, (by.get(g) ?? []).sort((x, y) => y.count - x.count)] as [string, Tally[]])
}

// Where an arrow key takes the focus in the list of acts, which is one stop in
// the tab order however many acts it holds (a chain can hold two hundred, and
// that was 193 presses of Tab to get past).
//
// Left and Right step through the acts in reading order and wrap, Home and End
// go to the ends, as the console's tab rows do. Up and Down go to the act
// nearest above or below on the screen: the chips wrap, so the row below is
// several steps on, and the gap between two groups is crossed the same way.
// boxes is where each act is drawn, in reading order. -1 means stay.
export type ActBox = { top: number; left: number; right: number }

export function actStep(key: string, at: number, boxes: ActBox[]): number {
  const n = boxes.length
  const here = boxes[at]
  if (!here) return -1
  if (key === 'ArrowRight') return (at + 1) % n
  if (key === 'ArrowLeft') return (at - 1 + n) % n
  if (key === 'Home') return 0
  if (key === 'End') return n - 1
  if (key !== 'ArrowDown' && key !== 'ArrowUp') return -1
  const down = key === 'ArrowDown'
  // Chips on one line share a top to within a pixel or two of rounding.
  const beyond = boxes.map((b, i) => ({ b, i })).filter(({ b }) => (down ? b.top > here.top + 4 : b.top < here.top - 4))
  if (beyond.length === 0) return -1
  const row = down ? Math.min(...beyond.map(({ b }) => b.top)) : Math.max(...beyond.map(({ b }) => b.top))
  const middle = (here.left + here.right) / 2
  let best = -1
  let gap = Infinity
  for (const { b, i } of beyond) {
    if (Math.abs(b.top - row) > 4) continue
    const d = Math.abs((b.left + b.right) / 2 - middle)
    if (d < gap) [best, gap] = [i, d]
  }
  return best
}

// Which act holds the list's one tab stop: the one last focused while it is
// still listed, else the first. Typing can take the last one off the list.
export function actStop(listed: string[], last: string | null): string | undefined {
  return last !== null && listed.includes(last) ? last : listed[0]
}

// What the bar holds and what the trail was read with, and when the second
// follows the first.
//
// A tick, a removed chip, Clear and Enter are read at once. A typed box is held
// at once and read when the typing pauses: the API matches an actor and a
// record exactly, so half an address matches nothing, and a read a letter is a
// screen that flickers through "no act matches" on the way to the answer.
//
// Plain code and not an effect, so a test can type into it, move the clock and
// see what was read (the second review, 11 October 2026: with the pause in an
// effect, a bar that never read what was typed passed every test).
export type BarState = { choices: AuditChoices; applied: AuditChoices }

export const NO_BAR: BarState = { choices: NO_CHOICES, applied: NO_CHOICES }

export type Clock = { set: (fn: () => void, ms: number) => unknown; clear: (timer: unknown) => void }

const WALL_CLOCK: Clock = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
}

export type FilterBar = {
  choose: (next: AuditChoices) => void
  type: (next: AuditChoices) => void
  stop: () => void
}

export function createFilterBar(publish: (s: BarState) => void, pauseMs: number, clock: Clock = WALL_CLOCK): FilterBar {
  let state = NO_BAR
  let waiting: unknown = null
  const put = (next: BarState) => {
    state = next
    publish(next)
  }
  const cancel = () => {
    if (waiting !== null) clock.clear(waiting)
    waiting = null
  }
  return {
    choose(next) {
      cancel()
      put({ choices: next, applied: next })
    },
    type(next) {
      cancel()
      put({ choices: next, applied: state.applied })
      waiting = clock.set(() => {
        waiting = null
        put({ choices: state.choices, applied: state.choices })
      }, pauseMs)
    },
    stop: cancel,
  }
}

// Whose days the two date boxes mean. The boxes hold calendar dates and a date
// is a day on the reader's clock: on or before the 7th runs to the reader's
// midnight, which is India's only for a reader in India. The control says so,
// two ways: a short name beside the label ("IST", "New York Time") and the
// zone's full name in the sentence that describes the control ("India
// Standard Time", "Eastern Time").
//
// Both are the zone's generic name, the one that is true of every date. For a
// day the label named today's offset, so a reader in Los Angeles in October
// saw "GMT-7" over December dates that were sent at 08:00 UTC (both reviews
// of 11 October 2026). An offset belongs to a date; a zone has a name.
//
// timeZone is the reader's unless a test names one.
function zonePart(style: 'shortGeneric' | 'longGeneric', timeZone?: string): string {
  const part = new Intl.DateTimeFormat('en-IN', { timeZoneName: style, timeZone }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')
  return part?.value ?? 'local time'
}

export function zoneWord(timeZone?: string): string {
  return zonePart('shortGeneric', timeZone)
}

export function zoneName(timeZone?: string): string {
  return zonePart('longGeneric', timeZone)
}
